import { Component, h, State, Element, Prop, Listen, getAssetPath } from '@stencil/core';
import { filterService } from '../../utils/services/filterService';
import { ComponentColors, initColors } from '../../utils/constants/colors';
import { z } from 'zod';
import { brandService } from '../../utils/services/brandService';
import { Brand } from '../../models/stores/Brand';
import { translationService } from '../../utils/services/translationService';


export const DropdownStyle = z.enum(['default', 'legacy']);
export type DropdownStyle = z.infer<typeof DropdownStyle>;
export const DropdownPosition = z.enum(['left', 'right', 'center']);
export type DropdownPosition = z.infer<typeof DropdownPosition>;
export const ButtonAppearance = z.enum(['text', 'bubble']);
export type ButtonAppearance = z.infer<typeof ButtonAppearance>;


export const ASL_FilterDropDownProps = z.object({
  dropdownStyle: DropdownStyle.optional(),
  dropdownPosition: DropdownPosition.optional(),
  buttonAppearance: ButtonAppearance.optional(),
  componentColors: ComponentColors.optional(),
});
export type ASL_FilterDropDownProps = z.infer<typeof ASL_FilterDropDownProps>;

@Component({
  tag: 'aico-store-locator-filter-dropdown',
  styleUrl: 'aico-store-locator-filter-dropdown.css',
  shadow: true,
  assetsDirs: ['../assets']
})
export class AicoStoreLocatorFilterDropdown {
  private unsubscribeController!: AbortController;
  private dropdownRef?: HTMLDivElement;
  private triggerRef?: HTMLElement;
  private isProcessingBrandToggle: boolean = false;

  @State() isOpen: boolean = false;
  @State() selectedBrands: number[] = [];

  /**
   * Determines the visual appearance of the filter trigger.
   * "text" (default) renders as simple text.
   * "bubble" renders as a bubble button with "Filter" written on it.
   */
  @Prop() buttonAppearance: ButtonAppearance = 'text';

  /**
   * Determines where the dropdown is anchored.
   * "left" (default) anchors it to the left.
   * "right" anchors it to the right.
   */
  @Prop() dropdownPosition: DropdownPosition = 'left';

  @Prop() dropdownStyle: DropdownStyle = 'default';

  @Prop() componentColors?: ComponentColors;
  @Element() el!: HTMLElement;

  @Prop() props?: ASL_FilterDropDownProps;

  async componentWillLoad() {

    if (this.props) {
      this.props = ASL_FilterDropDownProps.parse(this.props);
      this.buttonAppearance = this.props.buttonAppearance ?? this.buttonAppearance;
      this.dropdownPosition = this.props.dropdownPosition ?? this.dropdownPosition;
      this.dropdownStyle = this.props.dropdownStyle ?? this.dropdownStyle;
      this.componentColors = this.props.componentColors ?? this.componentColors;
    }

    await translationService.initComponentLoad('aico-store-locator-filter-dropdown');

    if (this.componentColors) {
      this.componentColors = ComponentColors.parse(this.componentColors);
      initColors(this.el, this.componentColors);
    }
    this.unsubscribeController?.abort();
    this.unsubscribeController = new AbortController();

    filterService.subscribe((filters) => {
      if (this.isProcessingBrandToggle) {
        this.isProcessingBrandToggle = false;
      }
      this.selectedBrands = filters;
    }, { signal: this.unsubscribeController.signal });
  }

  private t(key: string, locale?: string): string {
    return translationService.getTranslation('aico-store-locator-filter-dropdown', key, locale);
  }

  @Listen('click', { target: 'document', capture: true })
  handleClickOutside(event: MouseEvent) {
    if (!this.isOpen || this.isProcessingBrandToggle) return;

    // Get the composed path for handling shadow DOM correctly
    const path = event.composedPath();

    // Check if the click includes our component's elements
    const clickIncludesTrigger = this.triggerRef && path.includes(this.triggerRef);
    const clickIncludesDropdown = this.dropdownRef && path.includes(this.dropdownRef);

    // Only close if the click is outside both the trigger and the dropdown
    if (!clickIncludesTrigger && !clickIncludesDropdown) {
      this.isOpen = false;
    }
  }

  disconnectedCallback() {
    this.unsubscribeController?.abort();
  }

  private toggleDropdown() {
    this.isOpen = !this.isOpen;
  }

  private toggleBrand(brandId: number): void {
    this.isProcessingBrandToggle = true;

    filterService.toggleFilter(brandId);
  }

  private renderTrigger() {
    switch (this.buttonAppearance) {
      case 'bubble':
        return (
          <button
            ref={el => this.triggerRef = el}
            onClick={() => this.toggleDropdown()}
            class="flex items-center bg-[--primary-button-background-color] hover:bg-[--primary-button-hover-color] px-4 py-2 rounded-full text-[--primary-button-text-color] transition-colors"
          >
            <svg
              class="mr-2 w-5 h-5"
              viewBox="0 0 24 24"
              fill="currentColor"
              xmlns="http://www.w3.org/2000/svg">
              <path
                fill-rule="evenodd"
                clip-rule="evenodd"
                d="M15 10.5A3.502 3.502 0 0 0 18.355 8H21a1 1 0 1 0 0-2h-2.645a3.502 3.502 0 0 0-6.71 0H3a1 1 0 0 0 0 2h8.645A3.502 3.502 0 0 0 15 10.5zM3 16a1 1 0 1 0 0 2h2.145a3.502 3.502 0 0 0 6.71 0H21a1 1 0 1 0 0-2h-9.145a3.502 3.502 0 0 0-6.71 0H3z"
              />
            </svg>
            <span class="font-medium text-sm whitespace-nowrap">{this.t('Filter')}</span>
          </button>
        );
      default:
        return (
          <div
            ref={el => this.triggerRef = el}
            onClick={() => this.toggleDropdown()}
            class="inline-flex items-center p-4 text-[--primary-button-background-color] hover:text-[--primary-button-hover-color] transition-colors cursor-pointer"
          >
            <span class="font-medium text-sm whitespace-nowrap">
              {this.t('Filter by brands')}
            </span>
            <svg
              class={`h-5 w-5 transition-transform ${this.isOpen ? 'rotate-180' : ''}`}
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fill-rule="evenodd"
                d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                clip-rule="evenodd"
              />
            </svg>
          </div>
        );
    }
  }

  private renderDropdown() {
    const dropdownContainerClasses =
      this.dropdownPosition === 'right'
        ? 'right-4'
        : this.dropdownPosition === 'center'
          ? 'left-1/2 transform -translate-x-1/2'
          : 'left-4';

    switch (this.dropdownStyle) {
      case 'legacy':
        return (
          <div
            ref={el => this.dropdownRef = el}
            class={`absolute mt-2 ${dropdownContainerClasses} bg-[--background-color] rounded-lg shadow-lg z-50 w-auto min-w-72`}
          >
            <div class="p-2">
              {brandService.getAllBrandsOrderedByPriority().map((brand: Brand) => {
                const bId = Number(brand.brandId);
                const bName = brandService.getName(bId, translationService.getLocale()) || 'Unknown';
                const isSelected = !this.selectedBrands.includes(bId);
                const pin = brand.pinUrl;
                return (
                  <div
                    key={brand.brandId}
                    class="flex justify-between items-center bg-[--background-light-color] hover:bg-[--background-hover-color] p-2 rounded-md transition-colors"
                    onClick={() => this.toggleBrand(bId)}
                  >
                    <div class="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        class="border border-[--background-light-color] rounded-md w-5 h-5 text-[--primary-text-color] cursor-pointer"
                      />
                      <span class="ml-1 font-medium text-[--secondary-text-color] text-md">
                        {bName}
                      </span>
                    </div>

                    <img
                      src={getAssetPath(`${pin}`)}
                      alt={`${bName} logo`}
                      class="w-6 h-8"
                    />
                  </div>
                );
              })}
            </div>
          </div>
        );
      default:
        return (
          <div
            ref={el => this.dropdownRef = el}
            class={`absolute mt-2 ${dropdownContainerClasses} bg-[--background-color] rounded-lg shadow-lg z-50 w-auto min-w-max`}
          >
            <div class="p-2">
              {brandService.getAllBrandsOrderedByPriority().map((brand: Brand) => {
                const bId = Number(brand.brandId);
                const bName = brandService.getName(bId, translationService.getLocale()) || 'Unknown';
                const isSelected = !this.selectedBrands.includes(bId);
                const icon = brand.logoUrl;
                return (
                  <div
                    key={bId}
                    class={`flex items-center p-2 rounded-md cursor-pointer transition-colors ${isSelected ? 'bg-[--highlight-background-color] hover:bg-[--highlight-hover-color]' : 'hover:bg-[--background-hover-color]'
                      }`}
                    onClick={() => this.toggleBrand(bId)}
                  >
                    <div class="flex flex-1 items-center whitespace-nowrap">
                      <img
                        src={getAssetPath(`${icon}`)}
                        alt={`${bName} logo`}
                        class={`w-6 h-6 rounded-md mr-2 ${isSelected ? 'opacity-100' : 'opacity-50'}`}
                      />
                      <span class={`text-sm font-medium ${isSelected ? 'text-[--highlight-text-color]' : 'text-[--secondary-text-color]'}`}>
                        {bName}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
    }
  }

  render() {
    return (
      <div id="filter-dropdown" class="relative">
        {this.renderTrigger()}
        {this.isOpen && this.renderDropdown()}
      </div>
    );
  }
}
